import React, { useState } from 'react';
import { Check, Trash2, Edit2, X, Save } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { formatApiError } from '../../utils/apiError';
import { createTextPicker } from '../../utils/localizedContent';


const TIME_SLOTS = ["10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00","21:00"];

const STATUS_COLOR = {
  confirmed: 'bg-[#10B981]/20 text-[#34D399]',
  cancelled: 'bg-[#EF4444]/20 text-[#EF4444]',
  pending:   'bg-[#F59E0B]/20 text-[#F59E0B]',
  completed: 'bg-[#3B82F6]/20 text-[#3B82F6]',
};

export default function AdminTableReservations({ reservations, setReservations, lang, setMessage }) {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ date: '', time_slot: '', party_size: 2, status: 'confirmed', notes: '' });
  const l = createTextPicker(lang);
  const openNativeDatePicker = (event) => {
    const input = event.currentTarget;
    if (typeof input?.showPicker === 'function') input.showPicker();
  };

  const handleStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/table-reservations/${id}/status`, null, { params: { status } });
      setReservations(prev => prev.map(r => r.reservation_id === id ? { ...r, status } : r));
      setMessage(status === 'confirmed' ? l('Confirmada', 'Confirmed') : l('Cancelada', 'Cancelled'));
    } catch (err) { setMessage(formatApiError(err)); }
  };

  const handleCancel = async (id) => {
    if (!window.confirm(l('¿Cancelar esta reserva y avisar al usuario por email?', 'Cancel this reservation and notify user by email?'))) return;
    try {
      await axios.patch(`${API}/admin/table-reservations/${id}/status`, null, { params: { status: 'cancelled' } });
      setReservations(prev => prev.filter(r => r.reservation_id !== id));
      setMessage(l('Reserva cancelada', 'Reservation cancelled'));
    } catch (err) { setMessage(formatApiError(err)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(l('¿Borrar definitivamente esta reserva? Esta acción no se puede deshacer.', 'Delete this reservation permanently? This action cannot be undone.'))) return;
    try {
      await axios.delete(`${API}/admin/table-reservations/${id}`);
      setReservations(prev => prev.filter(r => r.reservation_id !== id));
      setMessage(l('Reserva borrada', 'Reservation deleted'));
    } catch (err) { setMessage(formatApiError(err)); }
  };

  const openEdit = (r) => {
    setEditingId(r.reservation_id);
    setEditForm({ date: r.date || '', time_slot: r.time_slot || '', party_size: r.party_size || 2, status: r.status || 'confirmed', notes: r.notes || '' });
  };

  const handleSave = async (id) => {
    try {
      await axios.patch(`${API}/admin/table-reservations/${id}/status`, null, { params: { status: editForm.status } });
      setReservations(prev => prev.map(r => r.reservation_id === id ? { ...r, ...editForm } : r));
      setMessage(l('Reserva actualizada', 'Reservation updated'));
      setEditingId(null);
    } catch (err) { setMessage(formatApiError(err)); }
  };

  const inputClass = "w-full bg-[#1C1C28] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C9A962]";

  return (
    <div>
      <h2 className="text-white font-bold text-xl mb-4">{l('Reservas de Mesa', 'Table Reservations')} ({reservations.length})</h2>
      <div className="space-y-3">
        {reservations.length === 0 && (
          <p className="text-[#A1A1AA] text-center py-8 text-sm">{l('No hay reservas de mesa', 'No table reservations')}</p>
        )}
        {reservations.map(r => (
          <div key={r.reservation_id} className="bg-[#12121A] border border-white/5 rounded-xl overflow-hidden">
            <div className="flex items-center gap-4 p-4">
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold">
                  {l('Mesa(s)', 'Table(s)')} {(r.table_numbers || [r.table_number]).filter(Boolean).join(', ') || '?'}
                </p>
                <p className="text-[#A1A1AA] text-sm">
                  {r.date} · {r.time_slot} · {r.duration}h · {r.party_size || 1} {l('pers.', 'ppl')}
                </p>
                <p className="text-[#52525B] text-xs">{r.user_name || r.user_email || 'Usuario'}</p>
                {r.linked_game_reservation ? (
                  <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-violet-500/15 border border-violet-500/30 text-violet-300 text-[11px]">
                    <span className="font-semibold">{l('Origen:', 'Origin:')}</span>
                    <span>
                      {l('Reserva de juego', 'Game reservation')}
                      {r.linked_game_name ? ` · ${r.linked_game_name}` : ''}
                      {r.linked_game_date && r.linked_game_time_slot ? ` (${r.linked_game_date} ${r.linked_game_time_slot})` : ''}
                      {` · ID ${r.linked_game_reservation}`}
                    </span>
                  </div>
                ) : (
                  <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-500/15 border border-slate-400/30 text-slate-300 text-[11px]">
                    <span className="font-semibold">{l('Origen:', 'Origin:')}</span>
                    <span>{l('Reserva solo de mesa (sin juego asociado)', 'Table-only reservation (no linked game)')}</span>
                  </div>
                )}
                {r.notes && <p className="text-[#A1A1AA] text-xs italic">{r.notes}</p>}
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${STATUS_COLOR[r.status] || 'bg-white/10 text-white'}`}>
                {r.status}
              </span>
              <div className="flex items-center gap-1 flex-shrink-0">
                {r.status === 'pending' && (
                  <button onClick={() => handleStatus(r.reservation_id, 'confirmed')}
                    className="p-1.5 rounded-lg text-[#10B981] hover:bg-green-500/10" title={l('Confirmar', 'Confirm')}>
                    <Check size={14} />
                  </button>
                )}
                <button
                  onClick={() => editingId === r.reservation_id ? setEditingId(null) : openEdit(r)}
                  className={`p-1.5 rounded-lg transition-colors ${
                    editingId === r.reservation_id ? 'text-[#C9A962] bg-[#C9A962]/10' : 'text-[#A1A1AA] hover:bg-[#C9A962]/10 hover:text-[#C9A962]'
                  }`} title={l('Editar', 'Edit')}>
                  <Edit2 size={14} />
                </button>
                <button onClick={() => handleCancel(r.reservation_id)}
                  className="p-1.5 rounded-lg text-[#F59E0B] hover:bg-amber-500/10" title={l('Cancelar', 'Cancel')}>
                  <X size={14} />
                </button>
                {/* Hard delete reservation */}
                <button onClick={() => handleDelete(r.reservation_id)}
                  className="p-1.5 rounded-lg text-[#EF4444] hover:bg-red-500/10" title={l('Eliminar', 'Delete')}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>

            {/* Edit panel */}
            {editingId === r.reservation_id && (
              <div className="border-t border-white/5 px-4 py-4 bg-[#0A0A0E]/40">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                  <div>
                    <label className="text-[#52525B] text-[10px] uppercase block mb-1">{l('Fecha', 'Date')}</label>
                    <input
                      type="date"
                      value={editForm.date}
                      onChange={e => setEditForm(f => ({ ...f, date: e.target.value }))}
                      onClick={openNativeDatePicker}
                      onFocus={openNativeDatePicker}
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label className="text-[#52525B] text-[10px] uppercase block mb-1">{l('Hora', 'Time')}</label>
                    <select value={editForm.time_slot} onChange={e => setEditForm(f => ({ ...f, time_slot: e.target.value }))} className={inputClass}>
                      {TIME_SLOTS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[#52525B] text-[10px] uppercase block mb-1">{l('Nº pers.', 'Party size')}</label>
                    <input type="number" min={1} max={10} value={editForm.party_size} onChange={e => setEditForm(f => ({ ...f, party_size: parseInt(e.target.value) || 1 }))} className={inputClass} />
                  </div>
                  <div>
                    <label className="text-[#52525B] text-[10px] uppercase block mb-1">{l('Estado', 'Status')}</label>
                    <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))} className={inputClass}>
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="text-[#52525B] text-[10px] uppercase block mb-1">{l('Notas', 'Notes')}</label>
                  <input value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} placeholder={l('Notas opcionales...', 'Optional notes...')} className={inputClass} />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleSave(r.reservation_id)} className="flex items-center gap-1.5 px-4 py-2 bg-[#C9A962] text-[#0A1612] rounded-lg text-sm font-semibold">
                    <Save size={13} />{l('Guardar', 'Save')}
                  </button>
                  <button onClick={() => setEditingId(null)} className="flex items-center gap-1.5 px-4 py-2 bg-white/5 text-[#A1A1AA] rounded-lg text-sm">
                    <X size={13} />{l('Cancelar', 'Cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
